/**
 * Accessibility Utilities
 * Provides utilities for implementing WCAG screen reader compatibility
 */

import type { ReactNode } from 'react';

/**
 * Announces a message to screen readers via an aria-live region
 * Use for dynamic content updates, loading states, errors
 * 
 * @param message - The message to announce
 * @param assertive - If true, uses aria-live="assertive" for urgent messages (errors, etc)
 *                    If false, uses aria-live="polite" for non-urgent updates
 * 
 * @example
 * ```tsx
 * // For error messages
 * announceToScreenReader('Error loading data', true);
 * 
 * // For loading updates
 * announceToScreenReader('Loading data, please wait', false);
 * ```
 */
export function announceToScreenReader(message: string, assertive = false): void {
  // Create or get the live region
  let liveRegion = document.querySelector(`[aria-live="${assertive ? 'assertive' : 'polite'}"]`);
  
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    document.body.appendChild(liveRegion);
  }

  // Clear and update content to trigger announcement
  (liveRegion as HTMLElement).textContent = '';
  
  // Use setTimeout to ensure screen readers pick up the change
  setTimeout(() => {
    (liveRegion as HTMLElement).textContent = message;
  }, 100);
}

/**
 * Creates a screen reader only message element
 * Used for additional context that shouldn't be visible to sighted users
 * 
 * @param text - The text to announce
 * @returns JSX element to be rendered
 * 
 * @example
 * ```tsx
 * <button>
 *   {srOnlyText('Sign up for power flow analysis')}
 *   Sign up
 * </button>
 * ```
 */
export function srOnlyText(text: string): ReactNode {
  return <span className="sr-only">{text}</span>;
}

/**
 * Announce loading state to screen readers
 * 
 * @param isLoading - Whether content is loading
 * @param content - Optional custom message
 */
export function announceLoadingState(isLoading: boolean, content = 'Loading content, please wait'): void {
  if (isLoading) {
    announceToScreenReader(content, false);
  }
}

/**
 * Announce error state to screen readers
 * 
 * @param error - Error message to announce
 * @param context - Optional context about what failed
 */
export function announceError(error: string, context = ''): void {
  const message = context ? `Error: ${error}. ${context}` : `Error: ${error}`;
  announceToScreenReader(message, true);
}

/**
 * Announce expand/collapse state
 * 
 * @param isExpanded - Whether section is expanded
 * @param sectionName - Name of the section
 */
export function announceExpandedState(isExpanded: boolean, sectionName: string): void {
  const state = isExpanded ? 'expanded' : 'collapsed';
  announceToScreenReader(`${sectionName} ${state}`, false);
}

/**
 * Get accessible name for element combining various sources
 * Follows WCAG naming hierarchy:
 * 1. aria-label
 * 2. aria-labelledby
 * 3. text content
 * 4. title attribute
 * 
 * @param element - The HTML element
 * @returns Accessible name or empty string
 */
export function getAccessibleName(element: HTMLElement): string {
  // Check aria-label
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) return ariaLabel;

  // Check aria-labelledby
  const ariaLabelledBy = element.getAttribute('aria-labelledby');
  if (ariaLabelledBy) {
    const labelElement = document.getElementById(ariaLabelledBy);
    if (labelElement) return labelElement.textContent || '';
  }

  // Check text content
  const textContent = element.textContent?.trim();
  if (textContent) return textContent;

  // Check title
  const title = element.getAttribute('title');
  if (title) return title;

  return '';
}

/**
 * Check if element has accessible name
 * 
 * @param element - The HTML element to check
 * @returns True if element has an accessible name
 */
export function hasAccessibleName(element: HTMLElement): boolean {
  return getAccessibleName(element).length > 0;
}

/**
 * Generate a unique ID for linking elements
 * 
 * @param prefix - Prefix for the ID
 * @returns Unique ID
 */
export function generateId(prefix = 'id'): string {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Focus on element and announce change
 * Useful for moving focus to new content
 * 
 * @param element - Element to focus
 * @param announcement - Optional announcement message
 */
export function focusElement(element: HTMLElement, announcement?: string): void {
  element.focus();
  if (announcement) {
    announceToScreenReader(announcement, false);
  }
}

/**
 * Get all focusable elements within a container
 * 
 * @param container - Container element
 * @returns Array of focusable elements
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selector = [
    'button',
    'a[href]',
    'input',
    'textarea',
    'select',
    '[tabindex]:not([tabindex="-1"])',
    '[role="button"]',
    '[role="link"]',
    '[role="menuitem"]',
  ].join(', ');

  return Array.from(container.querySelectorAll(selector)) as HTMLElement[];
}

/**
 * Trap focus within a container (useful for modals)
 * 
 * @param container - Container to trap focus in
 * @param callback - Function to call when escape is pressed
 */
export function trapFocus(container: HTMLElement, callback?: () => void): () => void {
  const focusableElements = getFocusableElements(container);
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  const handleKeyDown = (e: KeyboardEvent): void => {
    // Escape key
    if (e.key === 'Escape' && callback) {
      callback();
      return;
    }

    // Tab key
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift+Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  container.addEventListener('keydown', handleKeyDown);

  // Return cleanup function
  return () => {
    container.removeEventListener('keydown', handleKeyDown);
  };
}

/**
 * CSS class for screen reader only content
 * Should be added to stylesheet:
 * 
 * ```css
 * .sr-only {
 *   position: absolute;
 *   width: 1px;
 *   height: 1px;
 *   padding: 0;
 *   margin: -1px;
 *   overflow: hidden;
 *   clip: rect(0, 0, 0, 0);
 *   white-space: nowrap;
 *   border-width: 0;
 * }
 * ```
 */
export const SR_ONLY_CLASS = 'sr-only';

/**
 * ARIA attribute helpers
 */
export const ariaAttributes = {
  /**
   * Set element as required
   */
  setRequired(element: HTMLElement, required = true): void {
    if (required) {
      element.setAttribute('aria-required', 'true');
    } else {
      element.removeAttribute('aria-required');
    }
  },

  /**
   * Set element as invalid with optional error message
   */
  setInvalid(element: HTMLElement, invalid = true, errorMessage?: string): void {
    if (invalid) {
      element.setAttribute('aria-invalid', 'true');
      if (errorMessage) {
        element.setAttribute('aria-describedby', `error-${element.id}`);
      }
    } else {
      element.removeAttribute('aria-invalid');
      element.removeAttribute('aria-describedby');
    }
  },

  /**
   * Set aria-expanded for collapsible sections
   */
  setExpanded(element: HTMLElement, expanded: boolean): void {
    element.setAttribute('aria-expanded', expanded.toString());
  },

  /**
   * Set aria-checked for checkboxes
   */
  setChecked(element: HTMLElement, checked: boolean): void {
    element.setAttribute('aria-checked', checked.toString());
  },

  /**
   * Set aria-pressed for toggle buttons
   */
  setPressed(element: HTMLElement, pressed: boolean): void {
    element.setAttribute('aria-pressed', pressed.toString());
  },

  /**
   * Set aria-label
   */
  setLabel(element: HTMLElement, label: string): void {
    element.setAttribute('aria-label', label);
  },

  /**
   * Set aria-describedby
   */
  setDescription(element: HTMLElement, descriptionId: string): void {
    element.setAttribute('aria-describedby', descriptionId);
  },

  /**
   * Set aria-live region
   */
  setLiveRegion(element: HTMLElement, polite = true): void {
    element.setAttribute('aria-live', polite ? 'polite' : 'assertive');
    element.setAttribute('aria-atomic', 'true');
  },
};

/**
 * Validate heading hierarchy (no skipped levels)
 * 
 * @returns Array of issues found, empty if valid
 */
export function validateHeadingHierarchy(): string[] {
  const issues: string[] = [];
  const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
  
  // Check for multiple h1s
  const h1s = headings.filter(h => h.tagName === 'H1');
  if (h1s.length !== 1) {
    issues.push(`Expected 1 h1, found ${h1s.length}`);
  }

  // Check for skipped heading levels
  let previousLevel = 0;
  headings.forEach(heading => {
    const level = parseInt(heading.tagName[1]);
    if (level - previousLevel > 1) {
      issues.push(`Heading level skipped from h${previousLevel} to h${level}`);
    }
    previousLevel = level;
  });

  return issues;
}

/**
 * Check for keyboard accessibility issues
 * 
 * @returns Array of issues found
 */
export function checkKeyboardAccessibility(): string[] {
  const issues: string[] = [];
  const interactive = document.querySelectorAll('button, a, input, textarea, select, [role="button"]');

  interactive.forEach((element: Element) => {
    const el = element as HTMLElement;
    
    // Check if native interactive element
    const isNative = ['BUTTON', 'A', 'INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
    
    // Check if has role
    const hasRole = el.getAttribute('role');
    
    // Check if focusable
    if (!isNative && !hasRole && el.getAttribute('tabindex') === '-1') {
      issues.push(`Interactive element not keyboard accessible: ${el.className}`);
    }
  });

  return issues;
}
